import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import debounce from "lodash.debounce";
import * as Sentry from "@sentry/browser";
import Icon from "../Icon";
import Pager from "../Pager";
import {
  DateRange,
  MatchStatus,
  ReconFilters,
  ReconRow,
  RemarkLine,
  Role,
} from "../types";
import RerunOptionsModal from "../RerunOptionsModal";
import {
  fmtCcy,
  fmtNum,
  fmtPrice,
  fmtUnits,
  formatFriendly,
  signedUnits,
  productColor,
} from "../helpers";
import Remarks from "./Remarks";

import ActiveChip from "./ActiveChip";
import FilterPopover, { EMPTY_FILTERS, MATCH_LABELS } from "./FilterPopover";
import { request } from "../../../helpers/utilities";
import { GET_RECONCILIATION_ENTRIES } from "../../../gql/productStatements";
import { PageToolbar } from "../../../components/PageToolbar";
import RowProblemSummary from "./RowProblemSummary";
import {
  advancedConditions,
  entryFilters,
  EntryFiltersInput,
} from "./entryFilters";
import AdvancedSearchModal, {
  IField,
  AdvancedSearchReturn,
} from "../../../components/AdvancedSearchModal";

const SHOW_ITD = false;
const SHOW_COMPANIES = false;

interface ApiReconEntry {
  id: string;
  statementUploadId: string;
  statementUpload: { publicId: string; needsRerun?: boolean };
  needsRerun?: boolean;
  remarkLines: RemarkLine[] | null;
  priceDate: string;
  paymentsPriceDate: string | null;
  product: { id: string; name: string; fundCode: string };
  price: { id: string; amount: number } | null;
  uploadAmount: number;
  paymentsAmount: number | null;
  uploadUnits: number;
  paymentsUnits: number | null;
  expectedPaymentCount: number;
  matchedPaymentCount: number;
  missedPaymentCount: number;
  unitDiscrepancies: number;
  txCount: number;
  matchStatus: string;
  diffAmount: number | null;
  diffUnits: number | null;
  reversedAt: string | null;
  createdAt: string;
  runType: string;
  companiesCount: number;
  supersededCount: number;
}

interface EntriesSummary {
  entries: number;
  uploadAmount: number;
  paymentsAmount: number;
  uploadUnits: number;
  paymentsUnits: number;
  txCount: number;
  reversed: number;
}

interface ApiUploadSummary {
  entries: number;
  matchedCount: number;
  mismatchedCount: number;
  pendingCount: number;
  uploadAmount: number;
  paymentsAmount: number;
  txCount: number;
  productCount: number;
  reconciledAt: string;
}

interface EntriesPage {
  results: ApiReconEntry[];
  count: number;
  matchedCount: number;
  mismatchedCount: number;
  pendingCount: number;
  summary: EntriesSummary;
  uploadSummary: ApiUploadSummary | null;
  productOptions: Array<{ id: string; name: string }>;
  lastRunAt: string | null;
  lastRunEntries: number;
}

type EntriesResponse = {
  data: { data: { reconciliationEntries: EntriesPage | null } };
};

const EMPTY_SUMMARY: EntriesSummary = {
  entries: 0,
  uploadAmount: 0,
  paymentsAmount: 0,
  uploadUnits: 0,
  paymentsUnits: 0,
  txCount: 0,
  reversed: 0,
};

const EXPORT_PAGE_SIZE = 500;
const SEARCH_DELAY_MS = 300;

function apiEntryToLegacy(entry: ApiReconEntry): ReconRow {
  return {
    id: entry.id,
    product: entry.product.name,
    productId: entry.product.id,
    fundCode: entry.product.fundCode,
    statementUploadId: entry.statementUploadId,
    uploadPublicId: entry.statementUpload?.publicId ?? "",
    priceDate: entry.priceDate,
    paymentsPriceDate: entry.paymentsPriceDate || "",
    price: Number(entry.price?.amount ?? 0),
    amount: Number(entry.uploadAmount || 0),
    amountTA: Number(entry.paymentsAmount ?? 0),
    unitsPhx: Number(entry.uploadUnits || 0),
    unitsTA: Number(entry.paymentsUnits ?? 0),
    txCount: Number(entry.txCount || 0),
    expectedPaymentCount: entry.expectedPaymentCount,
    matchedPaymentCount: entry.matchedPaymentCount,
    missedPaymentCount: entry.missedPaymentCount,
    unitDiscrepancies: entry.unitDiscrepancies,
    created: entry.createdAt,
    match: entry.matchStatus as MatchStatus,
    diff: entry.diffAmount != null ? Number(entry.diffAmount) : undefined,
    diffUnits: entry.diffUnits != null ? Number(entry.diffUnits) : undefined,
    reversedAt: entry.reversedAt ?? undefined,
    runType: entry.runType,
    companiesCount: entry.companiesCount,
    supersededCount: entry.supersededCount,
    remarkLines: entry.remarkLines ?? [],
    needsRerun: !!entry.needsRerun,
  };
}

interface Props {
  role: Role;
  onOpenRow: (row: ReconRow) => void;
}

const OverviewTab: React.FC<Props> = ({ role, onOpenRow }) => {
  const [filters, setFilters] = useState<ReconFilters>(EMPTY_FILTERS);
  const [dateRange, setDateRange] = useState<DateRange>("latest");
  const [query, setQuery] = useState("");
  const [popOpen, setPopOpen] = useState(false);
  const [pageState, setPageState] = useState({ scope: "", page: 1 });
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState<ReconRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState({
    matched: 0,
    mismatched: 0,
    pending: 0,
  });
  const [summary, setSummary] = useState<EntriesSummary>(EMPTY_SUMMARY);
  const [serverUploadSummary, setServerUploadSummary] =
    useState<ApiUploadSummary | null>(null);
  const [productNames, setProductNames] = useState<string[]>([]);
  const [lastRun, setLastRun] = useState<{
    at: string | null;
    entries: number;
  }>({ at: null, entries: 0 });
  const [searchTerm, setSearchTerm] = useState("");
  const latestRequest = useRef(0);
  const [loading, setLoading] = useState(true);
  const [uploadFilter, setUploadFilter] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportBusy, setExportBusy] = useState<string | null>(null);
  const [rerunModalVisible, setRerunModalVisible] = useState(false);
  const [selectedRerunRow, setSelectedRerunRow] = useState<ReconRow | null>(
    null,
  );

  const [selectedFieldsToDisplay, setSelectedFieldsToDisplay] = useState<
    IField[]
  >(() => {
    const saved = localStorage.getItem("reconciliationEntry");
    return saved ? JSON.parse(saved) : [];
  });
  const [removedField, setRemovedField] = useState<IField | undefined>(
    undefined,
  );
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);

  const handleOnAdvancedSearchData = (
    _data: AdvancedSearchReturn,
    fields: IField[],
  ) => {
    setSelectedFieldsToDisplay([...fields]);
    setRemovedField(undefined);
    setShowAdvancedSearchModal(false);
    setLoading(false);
  };

  const handleOnFieldRemove = (field: IField) => {
    const nextFields = selectedFieldsToDisplay.filter(
      (f) => f.name !== field.name,
    );
    setRemovedField(field);
    setSelectedFieldsToDisplay(nextFields);
    if (nextFields.length === 0) {
      localStorage.removeItem("reconciliationEntry");
    } else {
      localStorage.setItem("reconciliationEntry", JSON.stringify(nextFields));
    }
  };

  const currentFilters = useCallback(
    (): EntryFiltersInput =>
      selectedFieldsToDisplay.length > 0
        ? { advancedFields: advancedConditions(selectedFieldsToDisplay) }
        : entryFilters({ filters, dateRange, query: searchTerm, uploadFilter }),
    [selectedFieldsToDisplay, filters, dateRange, searchTerm, uploadFilter],
  );

  const pageScope = useMemo(
    () => JSON.stringify([currentFilters(), pageSize]),
    [currentFilters, pageSize],
  );
  const page = pageState.scope === pageScope ? pageState.page : 1;
  const setPage = useCallback(
    (next: number) => setPageState({ scope: pageScope, page: next }),
    [pageScope],
  );

  const loadRows = useCallback(() => {
    setLoading(true);
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    request({
      query: GET_RECONCILIATION_ENTRIES,
      variables: { page, pageSize, filters: currentFilters() },
    })
      .then((response: EntriesResponse) => {
        if (requestId !== latestRequest.current) return;
        const data = response.data.data.reconciliationEntries;
        setRows((data?.results ?? []).map(apiEntryToLegacy));
        setTotalCount(data?.count ?? 0);
        setCounts({
          matched: data?.matchedCount ?? 0,
          mismatched: data?.mismatchedCount ?? 0,
          pending: data?.pendingCount ?? 0,
        });
        setSummary(data?.summary ?? EMPTY_SUMMARY);
        setServerUploadSummary(data?.uploadSummary ?? null);
        setProductNames(
          (data?.productOptions ?? []).map((product) => product.name),
        );
        setLastRun({
          at: data?.lastRunAt ?? null,
          entries: data?.lastRunEntries ?? 0,
        });
      })
      .catch((error: unknown) => {
        Sentry.captureException(error);
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
  }, [page, pageSize, currentFilters]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const pushSearch = useMemo(
    () => debounce((value: string) => setSearchTerm(value), SEARCH_DELAY_MS),
    [],
  );

  useEffect(() => {
    pushSearch(query);
  }, [query, pushSearch]);

  useEffect(() => () => pushSearch.cancel(), [pushSearch]);

  const products = productNames;
  const filteredRows = rows;
  const reversedCount = summary.reversed;
  const totalAmount = summary.uploadAmount;
  const totalTA = summary.paymentsAmount;
  const totalUnitsPhx = summary.uploadUnits;
  const totalUnitsTA = summary.paymentsUnits;
  const totalTx = summary.txCount;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filteredRows
    .slice()
    .sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
    );
  const { matched, mismatched, pending } = counts;
  const liveTotal = matched + mismatched + pending;
  const matchRate = liveTotal ? Math.round((matched / liveTotal) * 100) : 0;

  // The delta between Phoenix and the TA is the reconciliation payoff. Mirror
  // the table Totals-row logic so the headline and the table never disagree.
  const totalDelta = totalAmount - totalTA;
  const deltaIsZero = Math.abs(totalDelta) < 0.005;
  const deltaDisplay = deltaIsZero
    ? `GHS ${fmtCcy(0)}`
    : `${totalDelta < 0 ? "−" : "+"}GHS ${fmtCcy(Math.abs(totalDelta))}`;
  const verdictState =
    mismatched > 0 ? "is-danger" : pending > 0 ? "is-pending" : "is-success";

  const canEdit = role === "editor";

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const csvCell = (value: unknown): string => {
    if (value == null) return "";
    const text = String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const buildCsv = (scopedRows: ReconRow[]): string => {
    const headers = [
      "Upload",
      "Product",
      "Fund code",
      "Upload date",
      "Payments date",
      "Price",
      "Amount (Upload)",
      "Amount (Payments)",
      "Diff (Upload − Payments)",
      "Units (Upload)",
      "Units (Payments)",
      "Δ Units",
      "# Tx",
      "Match",
      "Remarks",
      "Reconciled at",
    ];
    const lines = [headers.join(",")];
    for (const row of scopedRows) {
      const unitsPhx = row.price ? row.amount / row.price : 0;
      const unitsTA = row.price ? row.amountTA / row.price : 0;
      lines.push(
        [
          row.uploadPublicId || "",
          row.product,
          row.fundCode || "",
          row.priceDate,
          row.paymentsPriceDate || "",
          row.price,
          row.amount,
          row.amountTA,
          (row.amount - row.amountTA).toFixed(2),
          unitsPhx.toFixed(4),
          unitsTA.toFixed(4),
          (unitsPhx - unitsTA).toFixed(4),
          row.txCount,
          row.match,
          (row.amount - row.amountTA).toFixed(2),
          row.created,
        ]
          .map(csvCell)
          .join(","),
      );
    }
    return lines.join("\r\n");
  };

  const fetchAllRows = async (
    filtersInput: EntryFiltersInput,
  ): Promise<ReconRow[]> => {
    const collected: ReconRow[] = [];
    for (let nextPage = 1; ; nextPage += 1) {
      const response: EntriesResponse = await request({
        query: GET_RECONCILIATION_ENTRIES,
        variables: {
          page: nextPage,
          pageSize: EXPORT_PAGE_SIZE,
          filters: filtersInput,
        },
      });
      const data = response.data.data.reconciliationEntries;
      const results = data?.results ?? [];
      collected.push(...results.map(apiEntryToLegacy));
      if (
        results.length < EXPORT_PAGE_SIZE ||
        collected.length >= (data?.count ?? 0)
      )
        return collected;
    }
  };

  const exportScope = (scope: "view" | "upload"): void => {
    setExportOpen(false);
    setExportBusy(scope);
    const filtersInput =
      scope === "upload" && uploadFilter
        ? { uploadPublicId: uploadFilter }
        : currentFilters();
    const stamp = new Date().toISOString().slice(0, 10);
    const tag = scope === "upload" ? uploadFilter : `view-${stamp}`;
    fetchAllRows(filtersInput)
      .then((scoped) => {
        const blob = new Blob([buildCsv(scoped)], {
          type: "text/csv;charset=utf-8",
        });
        downloadBlob(blob, `reconciliation-${tag}.csv`);
      })
      .catch((error: unknown) => {
        Sentry.captureException(error);
      })
      .finally(() => setExportBusy(null));
  };

  const uploadSummary =
    uploadFilter && serverUploadSummary
      ? {
          entries: serverUploadSummary.entries,
          matched: serverUploadSummary.matchedCount,
          mismatched: serverUploadSummary.mismatchedCount,
          pending: serverUploadSummary.pendingCount,
          amount: serverUploadSummary.uploadAmount,
          amountTA: serverUploadSummary.paymentsAmount,
          txCount: serverUploadSummary.txCount,
          reconciledAt: serverUploadSummary.reconciledAt,
          products: serverUploadSummary.productCount,
        }
      : null;

  const dateRangeOptions: Array<[DateRange, string]> = [
    ["latest", "Latest run"],
    ["today", "Today"],
    ["7d", "Last 7 days"],
    ["30d", "Last 30 days"],
    ["all", "All time"],
  ];

  const lastRunAt = lastRun.at ? formatFriendly(lastRun.at, true) : undefined;
  const entriesReconciled = lastRun.at ? lastRun.entries : undefined;

  return (
    <>
      <AdvancedSearchModal
        toggleModal={() => setShowAdvancedSearchModal(false)}
        showModal={showAdvancedSearchModal}
        model="reconciliationEntry"
        onError={(msg) => {
          console.error(msg);
          setLoading(false);
        }}
        onData={handleOnAdvancedSearchData}
        triggerField={removedField?.name}
        pageSize={pageSize}
        page={page}
        activeFields={selectedFieldsToDisplay}
      />
      <div
        className="recon-summary"
        role="group"
        aria-label="Reconciliation summary"
      >
        <div className={`recon-summary-item is-lead ${verdictState}`}>
          <div className="recon-summary-label">Match rate</div>
          <div className="recon-summary-value">{matchRate}%</div>
          <div className="recon-summary-sub">
            <span className="up">{matched}</span> of {liveTotal} matched
            {mismatched > 0 && (
              <>
                {" "}
                · <span className="down">{mismatched} off</span>
              </>
            )}
            {pending > 0 && <> · {pending} waiting</>}
          </div>
        </div>
        <div className="recon-summary-item">
          <div className="recon-summary-label">Upload total</div>
          <div className="recon-summary-value">GHS {fmtCcy(totalAmount)}</div>
          <div className="recon-summary-sub">
            {fmtNum(totalTx)} contributions
          </div>
        </div>
        <div className="recon-summary-item">
          <div className="recon-summary-label">Payments total</div>
          <div className="recon-summary-value">GHS {fmtCcy(totalTA)}</div>
          <div className="recon-summary-sub">as recorded in Phoenix</div>
        </div>
        <div
          className={`recon-summary-item recon-delta ${
            deltaIsZero ? "is-success" : "is-danger"
          }`}
        >
          <div className="recon-summary-label">Difference</div>
          <div className="recon-summary-value">{deltaDisplay}</div>
          <div className="recon-summary-sub">
            {deltaIsZero ? "balanced — books agree" : "Upload vs payments"}
          </div>
        </div>
        {lastRunAt && (
          <div className="recon-summary-meta">
            <span className="recon-summary-meta-label">Last run</span>
            <span className="recon-summary-meta-value">{lastRunAt}</span>
            {entriesReconciled != null && (
              <span className="recon-summary-meta-sub">
                {entriesReconciled.toLocaleString()} reconciled
              </span>
            )}
          </div>
        )}
      </div>

      <div className="card recon-card">
        <div className="card-head" style={{ justifyContent: "space-between" }}>
          <div>
            <div className="card-title">
              Reconciliation entries
              {!canEdit && (
                <span className="role-badge view">
                  <Icon name="shield" size={11} strokeWidth={2.5} /> View only
                </span>
              )}
            </div>
            <div className="card-sub">
              Remarks show the difference — <strong>0.00</strong> when
              everything matches.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-sm"
              onClick={loadRows}
              disabled={loading}
              title="Refresh entries"
            >
              <Icon
                name="refresh"
                size={14}
                style={
                  loading
                    ? { animation: "spin 0.8s linear infinite" }
                    : undefined
                }
              />{" "}
              Refresh
            </button>
            <div className="export-menu-wrap">
              <button
                className="btn btn-sm"
                onClick={() => setExportOpen((open) => !open)}
                disabled={summary.entries === 0 || !!exportBusy}
                aria-expanded={exportOpen}
                aria-haspopup="menu"
              >
                <Icon name="export" size={14} /> Export
                <span className="export-menu-caret">▾</span>
              </button>
              {exportOpen && (
                <>
                  <div
                    className="export-menu-backdrop"
                    onClick={() => setExportOpen(false)}
                  />
                  <div className="export-menu" role="menu">
                    {uploadFilter && (
                      <div className="export-menu-section">
                        <div className="export-menu-section-label">
                          Full upload
                        </div>
                        <button
                          className="export-menu-item"
                          onClick={() => exportScope("upload")}
                          role="menuitem"
                        >
                          <span className="export-menu-item-icon">CSV</span>
                          <span className="export-menu-item-text">
                            <strong>{uploadFilter}</strong>
                            <span>spreadsheet, all rows</span>
                          </span>
                        </button>
                      </div>
                    )}
                    <div className="export-menu-section">
                      <div className="export-menu-section-label">
                        Current view
                      </div>
                      <button
                        className="export-menu-item"
                        onClick={() => exportScope("view")}
                        role="menuitem"
                      >
                        <span className="export-menu-item-icon">CSV</span>
                        <span className="export-menu-item-text">
                          <strong>{summary.entries} rows</strong>
                          <span>matches what&rsquo;s on screen</span>
                        </span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <PageToolbar
          searchValue={query}
          onSearchChange={(event) => setQuery(event.target.value)}
          searchPlaceholder="Search upload ID, product or value date…"
          advancedSearch={{
            onOpen: () => setShowAdvancedSearchModal(true),
            activeFields: selectedFieldsToDisplay,
            onFieldRemove: handleOnFieldRemove,
          }}
          filters={
            <PageToolbar.Filters
              isFiltering={
                filters.matches.length > 0 ||
                dateRange !== "latest" ||
                !!query ||
                !!uploadFilter ||
                selectedFieldsToDisplay.length > 0
              }
              onClear={() => {
                setFilters(EMPTY_FILTERS);
                setDateRange("latest");
                setQuery("");
                setUploadFilter(null);
                setSelectedFieldsToDisplay([]);
                localStorage.removeItem("reconciliationEntry");
              }}
              configs={[
                {
                  label: "Match Status",
                  options: [
                    { label: "Matched", value: "matched" },
                    { label: "Mismatched", value: "mismatched" },
                    { label: "Pending", value: "pending" },
                  ],
                  selectedValues: filters.matches,
                  onChange: (values) => {
                    setFilters({
                      ...filters,
                      matches: values as MatchStatus[],
                    });
                  },
                  mode: "single",
                },
              ]}
            >
              <div className="date-range">
                <Icon name="calendar" size={13} strokeWidth={2.2} />
                {dateRangeOptions.map(([key, label]) => (
                  <button
                    key={key}
                    className={`date-range-opt ${
                      dateRange === key ? "active" : ""
                    }`}
                    onClick={() => setDateRange(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {uploadFilter && (
                <button
                  type="button"
                  className="upload-chip"
                  onClick={() => setUploadFilter(null)}
                  title="Clear upload filter"
                  style={{
                    borderColor: "var(--primary)",
                    color: "var(--primary)",
                  }}
                >
                  Upload: {uploadFilter} ✕
                </button>
              )}
              {reversedCount > 0 && (
                <button
                  type="button"
                  className={`status-chip ${
                    filters.includeReversed ? "active" : ""
                  }`}
                  onClick={() =>
                    setFilters({
                      ...filters,
                      includeReversed: !filters.includeReversed,
                    })
                  }
                  title="Toggle visibility of reversed entries"
                  style={{
                    marginLeft: 8,
                    height: 36,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    borderRadius: "var(--border-radius-base)",
                    border: "1px solid var(--border)",
                    background: "#fff",
                    padding: "0 12px",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--muted)",
                  }}
                >
                  <Icon name="refresh" size={11} strokeWidth={2.5} />
                  <span>
                    {filters.includeReversed
                      ? "Hide reversed"
                      : "Show reversed"}
                  </span>
                </button>
              )}
            </PageToolbar.Filters>
          }
        />

        {uploadSummary && (
          <div
            className="upload-summary"
            role="group"
            aria-label="Filtered upload summary"
          >
            <div className="upload-summary-id">
              <span className="upload-summary-tag">Upload</span>
              <span className="upload-summary-public-id">{uploadFilter}</span>
              <button
                type="button"
                className="upload-summary-clear"
                onClick={() => setUploadFilter(null)}
                title="Clear filter"
              >
                ✕
              </button>
            </div>
            <div className="upload-summary-stat">
              <span className="upload-summary-label">Reconciled</span>
              <span className="upload-summary-value">
                {formatFriendly(uploadSummary.reconciledAt, true)}
              </span>
            </div>
            <div className="upload-summary-stat">
              <span className="upload-summary-label">Entries</span>
              <span className="upload-summary-value">
                {uploadSummary.entries}
                <span className="upload-summary-sub">
                  {" "}
                  · {uploadSummary.products} products
                </span>
              </span>
            </div>
            <div className="upload-summary-stat">
              <span className="upload-summary-label">Outcome</span>
              <span className="upload-summary-value">
                <span className="up">{uploadSummary.matched} matched</span>
                {uploadSummary.mismatched > 0 && (
                  <>
                    {" "}
                    ·{" "}
                    <span className="down">{uploadSummary.mismatched} off</span>
                  </>
                )}
                {uploadSummary.pending > 0 && (
                  <>
                    {" "}
                    ·{" "}
                    <span className="warn">
                      {uploadSummary.pending} waiting
                    </span>
                  </>
                )}
              </span>
            </div>
            <div className="upload-summary-stat upload-summary-stat-totals">
              <span className="upload-summary-label">Upload vs payments</span>
              <span className="upload-summary-value">
                GHS {fmtCcy(uploadSummary.amount)}
                <span className="upload-summary-sub">
                  {" "}
                  vs GHS {fmtCcy(uploadSummary.amountTA)}
                </span>
                {Math.abs(uploadSummary.amount - uploadSummary.amountTA) >
                  0.01 && (
                  <span
                    className={`upload-summary-delta ${
                      uploadSummary.amount - uploadSummary.amountTA > 0
                        ? "is-over"
                        : "is-under"
                    }`}
                  >
                    {uploadSummary.amount - uploadSummary.amountTA > 0
                      ? "+"
                      : "−"}
                    {fmtCcy(
                      Math.abs(uploadSummary.amount - uploadSummary.amountTA),
                    )}
                  </span>
                )}
              </span>
            </div>
          </div>
        )}

        <div className="tbl-wrap">
          <table className="tbl tbl-wide">
            <thead>
              <tr>
                <th>Upload</th>
                <th>Product</th>
                <th title="Value date the contribution is priced on. ≠ marks when the payments recorded in Phoenix used a different date.">
                  Value date
                </th>
                {SHOW_ITD && <th>Run type</th>}
                <th title="When this reconciliation was run">Run date</th>
                <th className="num" title="Unit price on the value date">
                  Price
                </th>
                <th className="num">Amount</th>
                <th className="num">Units</th>
                <th
                  className="num"
                  title="Unit difference: Phoenix units minus Transfer Agent units"
                >
                  Δ Units
                </th>
                <th
                  className="num"
                  title="Number of transactions in this entry"
                >
                  # Tx
                </th>
                {SHOW_COMPANIES && (
                  <th className="num" title="Number of companies in this entry">
                    Companies
                  </th>
                )}
                <th className="num">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, index) => (
                <tr
                  key={index}
                  className={`row-clickable row-match-${row.match}${
                    row.reversedAt ? " row-reversed" : ""
                  }`}
                  onClick={() => onOpenRow(row)}
                  title={
                    row.reversedAt
                      ? `Reversed on ${formatFriendly(row.reversedAt, true)}${
                          row.reversedFromUploadId
                            ? ` via ${row.reversedFromUploadId}`
                            : ""
                        }`
                      : undefined
                  }
                >
                  <td>
                    {row.uploadPublicId ? (
                      <button
                        type="button"
                        className="upload-chip"
                        onClick={(event) => {
                          event.stopPropagation();
                          setUploadFilter(
                            uploadFilter === row.uploadPublicId
                              ? null
                              : row.uploadPublicId || null,
                          );
                        }}
                        title={
                          uploadFilter === row.uploadPublicId
                            ? "Clear filter"
                            : "Filter to this upload"
                        }
                      >
                        {row.uploadPublicId}
                      </button>
                    ) : (
                      <span style={{ color: "var(--muted)" }}>—</span>
                    )}
                  </td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        minWidth: 200,
                      }}
                    >
                      <span
                        className="product-dot"
                        style={{
                          background: productColor(
                            products.indexOf(row.product),
                          ),
                        }}
                      />
                      <span style={{ whiteSpace: "nowrap" }}>
                        {row.product}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span>{formatFriendly(row.priceDate)}</span>
                      {row.match !== "pending" &&
                        row.paymentsPriceDate &&
                        row.paymentsPriceDate !== row.priceDate && (
                          <span
                            className="ta-date-diff"
                            title={`Payments recorded ${formatFriendly(
                              row.paymentsPriceDate,
                            )}`}
                          >
                            ≠ {formatFriendly(row.paymentsPriceDate)}
                          </span>
                        )}
                    </div>
                  </td>
                  {SHOW_ITD && (
                    <td>
                      {row.runType === "weekly_inception_to_date" ? (
                        <span
                          className="badge badge-weekly"
                          style={{
                            background: "#e6f7ff",
                            color: "#1890ff",
                            border: "1px solid #91d5ff",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: "bold",
                          }}
                        >
                          ITD
                        </span>
                      ) : (
                        <span
                          className="badge badge-daily"
                          style={{
                            background: "#f5f5f5",
                            color: "#666",
                            border: "1px solid #d9d9d9",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: "bold",
                          }}
                        >
                          Daily
                        </span>
                      )}
                    </td>
                  )}
                  <td
                    style={{ whiteSpace: "nowrap" }}
                    title={formatFriendly(row.created, true)}
                  >
                    {formatFriendly(row.created)}
                    {!!row.supersededCount && (
                      <span
                        title={`Rerun ${row.supersededCount} time${
                          row.supersededCount > 1 ? "s" : ""
                        }`}
                        style={{
                          marginLeft: 8,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                          background: "var(--surface-2)",
                          color: "var(--ink-2)",
                          border: "1px solid var(--border)",
                          borderRadius: 10,
                          padding: "2px 7px",
                          fontSize: 11.5,
                          fontWeight: 700,
                          verticalAlign: "middle",
                        }}
                      >
                        <Icon name="refresh" size={10} strokeWidth={2.8} />
                        {row.supersededCount}
                      </span>
                    )}
                  </td>
                  <td className="num mono">{fmtPrice(row.price)}</td>
                  <td className="num mono">{fmtCcy(row.amount)}</td>
                  <td className="num mono">{fmtUnits(row.unitsPhx)}</td>
                  <td
                    className="num mono"
                    style={{
                      color:
                        row.match === "mismatched"
                          ? (row.diffUnits || 0) > 0
                            ? "#b35900"
                            : "var(--danger)"
                          : "var(--muted)",
                    }}
                  >
                    {row.match === "mismatched" && row.diffUnits != null
                      ? signedUnits(row.diffUnits)
                      : "0.00"}
                  </td>
                  <td className="num mono">{fmtNum(row.txCount)}</td>
                  {SHOW_COMPANIES && (
                    <td
                      className="num mono"
                      title={`${row.companiesCount ?? 0} companies`}
                    >
                      {row.companiesCount ?? 0}
                    </td>
                  )}
                  <td className="num">
                    {row.reversedAt ? (
                      <span
                        style={{ color: "var(--muted)", fontSize: "0.78rem" }}
                      >
                        {row.reversedFromUploadId
                          ? `Reversed via ${row.reversedFromUploadId}`
                          : "Reversed"}
                      </span>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Remarks row={row} />
                        {row.missedPaymentCount > 0 && (
                          <span
                            style={{ color: "var(--danger)", fontSize: 12 }}
                          >
                            {row.missedPaymentCount} missed
                          </span>
                        )}
                        <RowProblemSummary row={row} />
                        {row.needsRerun && (
                          <button
                            type="button"
                            className="bg-petra-blue"
                            style={{
                              marginTop: 4,
                              padding: "2px 6px",
                              fontSize: 11,
                              borderRadius: 4,
                              color: "white",
                              border: "none",
                              fontWeight: 600,
                              cursor: "pointer",
                              width: "fit-content",
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedRerunRow(row);
                              setRerunModalVisible(true);
                            }}
                          >
                            ⚠️ Rerun
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {summary.entries > 0 && (
                <tr style={{ background: "var(--surface-2)", fontWeight: 600 }}>
                  <td
                    colSpan={5}
                    style={{ textAlign: "right", color: "var(--muted)" }}
                  >
                    Totals ({summary.entries} active entries)
                  </td>
                  <td className="num mono">{fmtCcy(totalAmount)}</td>
                  <td className="num mono">{fmtUnits(totalUnitsPhx)}</td>
                  <td
                    className="num mono"
                    style={{
                      color:
                        Math.abs(totalUnitsPhx - totalUnitsTA) > 0.01
                          ? "var(--danger)"
                          : "var(--success)",
                    }}
                  >
                    {signedUnits(totalUnitsPhx - totalUnitsTA)}
                  </td>
                  <td className="num mono">{fmtNum(totalTx)}</td>
                  <td
                    className="num mono"
                    style={{
                      color:
                        Math.abs(totalAmount - totalTA) > 0.01
                          ? "var(--danger)"
                          : "var(--success)",
                    }}
                  >
                    {fmtCcy(totalAmount - totalTA)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {loading && rows.length === 0 && (
          <div className="empty">
            <div className="empty-illustration">
              <Icon
                name="refresh"
                size={28}
                strokeWidth={2}
                style={{ animation: "spin 0.8s linear infinite" }}
              />
            </div>
            <div className="empty-title">Loading reconciliation entries…</div>
            <div>Fetching the latest payment reconciliation data.</div>
          </div>
        )}
        {!loading && rows.length === 0 && products.length === 0 && (
          <div className="empty">
            <div className="empty-illustration">
              <Icon name="clock" size={36} strokeWidth={1.5} />
            </div>
            <div className="empty-title">No reconciliation has run yet</div>
            <div>
              Upload a payment statement on the Uploads tab — it’ll reconcile
              automatically in the evening run at 7:00 PM.
            </div>
          </div>
        )}
        {!loading && rows.length === 0 && products.length > 0 && (
          <div className="empty">
            <div className="empty-illustration">
              <Icon name="inbox" size={36} strokeWidth={1.5} />
            </div>
            <div className="empty-title">Nothing matches those filters</div>
            <div>Try widening the date range or clearing a filter.</div>
          </div>
        )}

        {filteredRows.length > 0 && (
          <Pager
            page={safePage}
            pageSize={pageSize}
            total={totalCount}
            shownCount={pageRows.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
        <RerunOptionsModal
          visible={rerunModalVisible}
          onClose={() => setRerunModalVisible(false)}
          row={selectedRerunRow}
          onRerunScheduled={loadRows}
        />
      </div>
    </>
  );
};

export default OverviewTab;
